import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const ParticleField = ({ count = 200, isDarkMode = false }) => {
  const particlesRef = useRef();
  const positions = useRef(new Float32Array(count * 3));
  const colors = useRef(new Float32Array(count * 3));
  const symbols = useRef(new Array(count).fill().map(() => 
    Math.random() > 0.7 ? '$' : 
    Math.random() > 0.5 ? '%' : 
    Math.random() > 0.3 ? '€' : '¥'
  ));

  React.useEffect(() => {
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const radius = 15 + Math.random() * 10; // Larger spread
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      positions.current[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positions.current[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions.current[i3 + 2] = radius * Math.cos(phi);
      
      if (isDarkMode) {
        // Gold gradient for dark mode
        colors.current[i3] = 0.64 + Math.random() * 0.1;
        colors.current[i3 + 1] = 0.48 + Math.random() * 0.1;
        colors.current[i3 + 2] = 0.0 + Math.random() * 0.1;
      } else {
        // Brighter gold for light mode
        colors.current[i3] = 0.8 + Math.random() * 0.1;
        colors.current[i3 + 1] = 0.6 + Math.random() * 0.1;
        colors.current[i3 + 2] = 0.2 + Math.random() * 0.1;
      }
    }
  }, [count, isDarkMode]);

  useFrame((state) => {
    if (particlesRef.current) {
      particlesRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.05) * 0.1;
      particlesRef.current.rotation.y = state.clock.elapsedTime * 0.02;
      
      const positionsArray = particlesRef.current.geometry.attributes.position.array;
      for (let i = 0; i < count; i++) {
        const i3 = i * 3;
        positionsArray[i3 + 1] += Math.sin(state.clock.elapsedTime * 0.5 + i) * 0.003;
      }
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions.current}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={count}
          array={colors.current}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={isDarkMode ? 0.12 : 0.1}
        vertexColors
        transparent
        opacity={isDarkMode ? 0.9 : 0.7}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

export default ParticleField;